import { isDesktopRuntime } from './desktopDetection';
import { openExternal } from './desktopOpenExternal';

/**
 * Where a hand-off to a third party went: the page itself left (`navigated`), or the address went
 * to the system browser and this page stays on screen (`browser`).
 */
export type ThirdPartyHandOffType = 'navigated' | 'browser';

/**
 * Leaves for a third party — an OAuth provider, Stripe Checkout or the billing portal (I-1078,
 * ADR-160 AC-18).
 *
 * In a browser tab the page navigates there, as it always has, and is replaced. In the desktop app
 * the address goes to the system browser explicitly: the round trip finishes there and comes back
 * through a `featit-return` link (AC-9 to AC-17). The window's navigation guard would refuse a
 * `window.location.assign` and open it there anyway (AC-3), but then nothing on this page learns
 * the page was never replaced — a button set to spin until the page goes away spins forever. The
 * answer says which happened, so the caller can say where the flow continues.
 *
 * In the desktop app the window that leaves is noted first (I-1436, AC-30), so the return comes
 * back to it rather than to `main`.
 */
export async function leaveForThirdParty(url: string): Promise<ThirdPartyHandOffType> {
  if (isDesktopRuntime()) {
    await noteDesktopReturnOrigin();
    await openExternal(url);
    return 'browser';
  }
  window.location.assign(url);
  return 'navigated';
}

/**
 * Tells the Rust side this window is the one a third-party flow leaves from (I-1436, ADR-160
 * AC-30). Best-effort: without it the return still arrives, in the Dock's window instead of this one.
 */
async function noteDesktopReturnOrigin(): Promise<void> {
  try {
    const { invoke } = await import('@tauri-apps/api/core');
    await invoke('note_desktop_return_origin');
  } catch {
    // An older shell without the command, or a fixture without the bridge: the return falls back.
  }
}
